import { FC } from 'react';

import { push } from 'connected-react-router';
import { getAuctionTrlState } from 'utils/campaign';
import {
    CampaignCreationWizardStep,
    getCampaignCreationWizardAuctionProgress,
    isCampaignCreationWizardHistoryState,
} from 'utils/campaignCreationWizardFlow/campaignCreationWizardFlow';
import { CampaignProductType } from 'utils/constants/campaign';
import { NON_BREAKING_SPACE } from 'utils/constants/common';
import { MagritteNotificationName } from 'utils/constants/notification';
import { getClickmePromoDiscountedPrice } from 'utils/promoBonus';

import auctionCampaignBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/auction/auction_campaign_back_button_click';
import creationWizardPlacementBackButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_placement_back_button_click';
import creationWizardToCampaignsButtonClick from '@hh.ru/analytics-js-events/build/clickme/employer/campaign_menu/main_page/creation_wizard_to_campaigns_button_click';
import { ActionBar, Button, ProgressBar, useBreakpoint } from '@hh.ru/magritte-ui';

import { useAuctionBidValidation } from 'hooks/auction/useAuctionBidValidation';
import { useAuctionBudgetValidation } from 'hooks/auction/useAuctionBudgetValidation';
import useTrls from 'hooks/translation/useTrls';
import { useAvailableFirstCampaignAuctionPromo } from 'hooks/useAvailableFirstCampaignAuctionPromo';
import { useCampaignCreationWizardBackAlert } from 'hooks/useCampaignCreationWizardBackAlert';
import { useCampaignCreationWizardStepNavigation } from 'hooks/useCampaignCreationWizardStepNavigation';
import { useDashboardRoute } from 'hooks/useDashboardRoute';

import FormatMoney from 'components/FormatMoney';

import { startAuction } from 'components/AuctionBuilder/AuctionActions/startAuction';
import { updateAuction } from 'components/AuctionBuilder/AuctionActions/updateAuction';
import { useAuctionDateValidation } from 'components/AuctionBuilder/AuctionPeriod/useAuctionDateValidation';

import { AuctionDataField, auctionSelector, auctionSelectorNonNullable } from 'store/models/auctionData';
import { addMagritteNotification } from 'store/models/notification';
import { useDispatch, useSelector } from 'store/utils';

import styles from 'components/CampaignCreationWizardFlow/CampaignCreationWizardProgress.module.less';

const TrlKeys = {
    submit: {
        create: 'AuctionBuilder.AdForm.start',
        edit: 'AuctionBuilder.edit.submit',
        restart: 'AuctionBuilder.restart.submit',
    },
    back: 'AuctionBuilder.AuctionActions.back',
    toAds: 'CampaignCreationWizard.actionBar.toAds',
};

interface AuctionActionsProps {
    loading: boolean;
    setLoading: (loading: boolean) => void;
    showBalanceReplenishmentModal: () => void;
}

export const AuctionActions: FC<AuctionActionsProps> = ({ loading, setLoading, showBalanceReplenishmentModal }) => {
    const isWizardFlow = useSelector((state) => isCampaignCreationWizardHistoryState(state.router.location.state));
    const auctionCampaign = useSelector(auctionSelector.auctionCampaign);
    const auctionTrlState = getAuctionTrlState(auctionCampaign);
    const isAuctionCreate = auctionCampaign === null;
    const campaignId = auctionCampaign?.campaignId;
    const vacancyId = useSelector(auctionSelector.vacancyId);
    const vacancy = useSelector(auctionSelector.vacancy);
    const bidValue = useSelector(auctionSelectorNonNullable.bid).value;
    const budgetValue = useSelector(auctionSelectorNonNullable.budget).value;
    const startDate = useSelector(auctionSelector.startDate);
    const endDate = useSelector(auctionSelector.endDate);

    const trls = useTrls();
    const dispatch = useDispatch();
    const { isMobile } = useBreakpoint();
    const dashboardRoute = useDashboardRoute();
    const validateBid = useAuctionBidValidation();
    const validateBudget = useAuctionBudgetValidation();
    const validateDate = useAuctionDateValidation();

    const { isPromoAvailable, maxBonusAmount, promoSumPercent } = useAvailableFirstCampaignAuctionPromo();
    const isWizardFlowEnabled = isAuctionCreate || isWizardFlow;
    const { backAlert, openBackAlert } = useCampaignCreationWizardBackAlert();
    const goToWizardStep = useCampaignCreationWizardStepNavigation();

    const onSubmitClick = () => {
        const isBidValid = validateBid(bidValue, budgetValue);
        const isBudgetValid = validateBudget(budgetValue);
        const isStartDateValid = validateDate(startDate, AuctionDataField.StartAt);
        const isEndDateValid = validateDate(endDate, AuctionDataField.EndAt);

        if (!isBidValid || !isBudgetValid || !isStartDateValid || !isEndDateValid) {
            dispatch(addMagritteNotification(MagritteNotificationName.AuctionBuilderValidationError));
            return;
        }

        void dispatch(
            campaignId
                ? updateAuction(campaignId, setLoading, showBalanceReplenishmentModal, dashboardRoute)
                : startAuction(setLoading, dashboardRoute)
        );
    };

    const auctionWizardState = {
        vacancyId: vacancyId ? Number(vacancyId) : null,
        vacancyName: vacancy?.name,
        productType: CampaignProductType.SearchAuction,
    };

    const handleBackClick = () => {
        if (isWizardFlowEnabled) {
            creationWizardPlacementBackButtonClick({ placementType: 'TOP_SEARCH' });
        }
        auctionCampaignBackButtonClick({ vacancyId, campaignId });

        if (isWizardFlowEnabled) {
            if (!isAuctionCreate) {
                goToWizardStep(CampaignCreationWizardStep.PromotionType, auctionWizardState);
                return;
            }

            openBackAlert(() => goToWizardStep(CampaignCreationWizardStep.PromotionType, auctionWizardState));
            return;
        }

        dispatch(push(dashboardRoute));
    };

    const handleToAdsClick = () => {
        creationWizardToCampaignsButtonClick();
        if (isWizardFlowEnabled && isAuctionCreate) {
            openBackAlert(() => dispatch(push(dashboardRoute)));
        } else {
            dispatch(push(dashboardRoute));
        }
    };

    const progressConfig = getCampaignCreationWizardAuctionProgress();
    const progress = (
        <ProgressBar
            progress={progressConfig.progress}
            segments={progressConfig.segments}
            data-qa="campaign-creation-wizard-progress"
        />
    );
    const finalPrice =
        isAuctionCreate && isPromoAvailable
            ? getClickmePromoDiscountedPrice(budgetValue || 0, promoSumPercent, maxBonusAmount)
            : budgetValue;

    return (
        <>
            <ActionBar
                type={isMobile ? 'vertical' : 'horizontal'}
                isSticky
                showProgress={isWizardFlowEnabled ? progress : undefined}
                additionalContent={
                    isMobile && isWizardFlowEnabled ? <div className={styles.progress}>{progress}</div> : null
                }
                primaryActions={
                    <>
                        <Button data-qa="auction-wizard-back" mode="secondary" style="accent" onClick={handleBackClick}>
                            {trls(TrlKeys.back)}
                        </Button>
                        <Button
                            data-qa={campaignId ? 'edit_auction' : 'create_auction'}
                            disabled={loading}
                            mode="primary"
                            style="accent"
                            onClick={onSubmitClick}
                            postfix={
                                isAuctionCreate && finalPrice ? (
                                    <FormatMoney inCents groupSeparator={NON_BREAKING_SPACE}>
                                        {finalPrice}
                                    </FormatMoney>
                                ) : undefined
                            }
                            spaceBetween={false}
                        >
                            {trls(TrlKeys.submit[auctionTrlState])}
                        </Button>
                    </>
                }
                secondaryActions={
                    !isMobile && (
                        <Button
                            mode="secondary"
                            style="accent"
                            onClick={handleToAdsClick}
                            data-qa="auction-wizard-to-ads"
                        >
                            {trls(TrlKeys.toAds)}
                        </Button>
                    )
                }
            />
            {isWizardFlowEnabled && isAuctionCreate && backAlert}
        </>
    );
};
